import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, List as ListIcon, Plus } from "lucide-react";
import dayjs, { type Dayjs } from "dayjs";

import { Button, Chip, ChipRow } from "@/ui";
import { EVENT_COLOR, EVENT_COLOR_SOFT } from "@/lib/eventColors";
import { EventCalendar, type CalendarItem } from "@/features/events/EventCalendar";
import { EventCard, type EventCardData } from "@/features/events/EventCard";
import { EventDetailSheet } from "@/features/events/EventDetailSheet";
import { ChallengeCard } from "@/features/challenges/ChallengeCard";
import { ChallengeDetailSheet } from "@/features/challenges/ChallengeDetailSheet";
import { CreateChallengeDialog } from "@/features/challenges/CreateChallengeDialog";
import {
  useAcceptInvite,
  useChallenges,
  useDeclineInvite,
  useMyInvites,
  useTournaments,
  useTrainings,
  type TournamentInvite,
} from "@/features/queries";
import { useAuth } from "@/contexts/AuthContext";
import type { Challenge, Tournament, Training } from "@/types/api";

type Filter = "all" | "tournaments" | "trainings" | "challenges";
type ViewMode = "list" | "calendar";

/**
 * Bucket events so the list reads top-down as:
 *   0 — идёт прямо сейчас (турнир в процессе),
 *   1 — открыта запись (или активная тренировка в будущем),
 *   2 — завершённые,
 *   3 — отменённые.
 * Within a bucket we sort by date (earliest first, except completed → newest first).
 */
function statusBucket(e: EventCardData): number {
  if (e.kind === "tournament") {
    switch (e.data.status) {
      case "in_progress": return 0;
      case "draft":
      case "registration":
      case "full":       return 1;
      case "completed":  return 2;
      case "cancelled":  return 3;
      default:           return 1;
    }
  }
  // Trainings have no "in_progress" — an active training that already started
  // counts as "ongoing" and sticks to the top; future active ones go to
  // the registration bucket.
  const started = new Date(e.data.date_time).getTime() <= Date.now();
  switch (e.data.status) {
    case "active":     return started ? 0 : 1;
    case "completed":  return 2;
    case "cancelled":  return 3;
    default:           return 1;
  }
}

const EVENT_BUCKET_LABEL: Record<number, string> = {
  0: "Идёт сейчас",
  1: "Скоро",
  2: "Завершённые",
  3: "Отменённые",
};

const CHALLENGE_BUCKET_LABEL: Record<number, string> = {
  0: "В игре",
  1: "Открытые вызовы",
  2: "Завершённые",
  3: "Отменённые",
};

export function EventsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filter, setFilter] = useState<Filter>("all");
  const [view, setView] = useState<ViewMode>("list");
  const [calendarDay, setCalendarDay] = useState<Dayjs | null>(null);
  const [selected, setSelected] = useState<EventCardData | null>(null);
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null);
  const [challengeDialogOpen, setChallengeDialogOpen] = useState(false);

  const tournaments = useTournaments();
  const trainings = useTrainings();
  const challengesQ = useChallenges();

  const canCreateTraining = !!user && (user.is_admin || user.is_trainer);
  const showCreateTraining = canCreateTraining && filter === "trainings";
  const showCreateTournament = !!user && user.is_admin && filter === "tournaments";
  const showCreateChallenge = !!user && filter === "challenges";

  const events = useMemo<EventCardData[]>(() => {
    const t: EventCardData[] = (tournaments.data ?? [])
      .filter((data) => data.status !== "draft")
      .map((data) => ({ kind: "tournament", data }));
    const tr: EventCardData[] = (trainings.data ?? []).map((data) => ({
      kind: "training",
      data,
    }));
    const all = [...t, ...tr].sort((a, b) => {
      const ab = statusBucket(a);
      const bb = statusBucket(b);
      if (ab !== bb) return ab - bb;
      const at = new Date(a.data.date_time).getTime();
      const bt = new Date(b.data.date_time).getTime();
      // Inside the "completed" bucket, show most recent first — older events
      // drift to the bottom. Everywhere else, earliest upcoming comes first.
      return ab === 2 ? bt - at : at - bt;
    });
    if (filter === "tournaments") return all.filter((e) => e.kind === "tournament");
    if (filter === "trainings") return all.filter((e) => e.kind === "training");
    return all;
  }, [filter, tournaments.data, trainings.data]);

  const challengeRank = (c: Challenge): number => {
    if (c.status === "accepted") return 0;
    if (c.status === "pending") return 1;
    if (c.status === "completed") return 2;
    return 3;
  };

  const challengeList = useMemo(() => {
    const rows = [...(challengesQ.data ?? [])];
    // Top: pending + accepted (active), then everything else — completed
    // first of the "done" group so recent plays stay near the top.
    return rows.sort((a, b) => {
      const ra = challengeRank(a);
      const rb = challengeRank(b);
      if (ra !== rb) return ra - rb;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [challengesQ.data]);

  const isLoading =
    filter === "challenges"
      ? challengesQ.isLoading
      : tournaments.isLoading || trainings.isLoading;

  // Build the calendar dataset. `mine=true` marks events the user either
  // created (training organiser) or is participating in (registered for a
  // tournament, challenger/challenged on a challenge) so the calendar can
  // ring those days subtly.
  const calendarItems = useMemo<CalendarItem[]>(() => {
    const out: CalendarItem[] = [];
    const uid = user?.id ?? null;

    if (filter === "all" || filter === "tournaments") {
      for (const t of tournaments.data ?? []) {
        if (t.status === "draft") continue;
        out.push({
          kind: "tournament",
          id: t.id,
          date: dayjs(t.date_time),
          mine: !!uid && (t.is_registered || t.created_by === uid),
          source: t,
        });
      }
    }
    if (filter === "all" || filter === "trainings") {
      for (const tr of trainings.data ?? []) {
        out.push({
          kind: "training",
          id: tr.id,
          date: dayjs(tr.date_time),
          mine: !!uid && (!!tr.is_registered || tr.created_by === uid),
          source: tr,
        });
      }
    }
    if (filter === "all" || filter === "challenges") {
      for (const c of challengesQ.data ?? []) {
        // Challenges without a scheduled_at are "play right now" — anchor
        // them to created_at so they still land on a calendar day.
        const d = c.scheduled_at ?? c.created_at;
        out.push({
          kind: "challenge",
          id: c.id,
          date: dayjs(d),
          mine: !!uid && (c.challenger.id === uid || c.challenged.id === uid),
          source: c,
        });
      }
    }
    return out;
  }, [filter, tournaments.data, trainings.data, challengesQ.data, user?.id]);

  const daySelected = calendarDay ?? dayjs();
  const dayItems = useMemo(() => {
    const key = daySelected.format("YYYY-MM-DD");
    return calendarItems.filter((it) => it.date.format("YYYY-MM-DD") === key);
  }, [calendarItems, daySelected]);

  return (
    <div style={{ paddingTop: 8, paddingBottom: 24 }}>
      <InvitesBanner />

      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 16 }}>
        <ViewToggle view={view} onChange={setView} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <ChipRow>
            <Chip active={filter === "all"} onClick={() => setFilter("all")}>
              Все
            </Chip>
            <Chip
              active={filter === "tournaments"}
              color={EVENT_COLOR.tournament}
              colorSoft={EVENT_COLOR_SOFT.tournament}
              onClick={() => setFilter("tournaments")}
            >
              Турниры
            </Chip>
            <Chip
              active={filter === "trainings"}
              color={EVENT_COLOR.training}
              colorSoft={EVENT_COLOR_SOFT.training}
              onClick={() => setFilter("trainings")}
            >
              Тренировки
            </Chip>
            <Chip
              active={filter === "challenges"}
              color={EVENT_COLOR.challenge}
              colorSoft={EVENT_COLOR_SOFT.challenge}
              onClick={() => setFilter("challenges")}
            >
              Вызовы
            </Chip>
          </ChipRow>
        </div>
        {showCreateTraining && (
          <CreateFab
            title="Создать тренировку"
            onClick={() =>
              navigate("/admin/trainings/new", { state: { from: "/events" } })
            }
          />
        )}
        {showCreateTournament && (
          <CreateFab
            title="Создать турнир"
            onClick={() =>
              navigate("/admin/tournaments/new", { state: { from: "/events" } })
            }
          />
        )}
        {showCreateChallenge && (
          <CreateFab
            title="Бросить вызов"
            onClick={() => setChallengeDialogOpen(true)}
          />
        )}
      </div>

      {view === "calendar" ? (
        <>
          <EventCalendar
            items={calendarItems}
            selected={calendarDay}
            onSelect={setCalendarDay}
          />
          {isLoading ? (
            <EmptyState title="Загрузка…" />
          ) : dayItems.length === 0 ? (
            <EmptyState
              title={daySelected.format("D MMMM")}
              subtitle="В этот день событий нет."
            />
          ) : (
            <>
              <BucketDivider
                label={daySelected.format("D MMMM, dddd")}
                first
              />
              {dayItems.map((it) => {
                if (it.kind === "challenge") {
                  const c = it.source as Challenge;
                  return (
                    <ChallengeCard
                      key={`ch-${c.id}`}
                      challenge={c}
                      onClick={() => setSelectedChallenge(c)}
                    />
                  );
                }
                const e: EventCardData =
                  it.kind === "tournament"
                    ? { kind: "tournament", data: it.source as Tournament }
                    : { kind: "training", data: it.source as Training };
                return (
                  <EventCard
                    key={`${it.kind}-${it.id}`}
                    event={e}
                    onClick={() => setSelected(e)}
                  />
                );
              })}
            </>
          )}
        </>
      ) : filter === "challenges" ? (
        isLoading ? (
          <EmptyState title="Загрузка…" />
        ) : challengeList.length === 0 ? (
          <EmptyState
            title="Пока никто никого не вызывал"
            subtitle="Нажми «+», чтобы бросить первый вызов."
          />
        ) : (
          challengeList.map((c, i) => {
            const b = challengeRank(c);
            const prevRow = i > 0 ? challengeList[i - 1] : undefined;
            const prev = prevRow ? challengeRank(prevRow) : null;
            const label = CHALLENGE_BUCKET_LABEL[b] ?? "";
            return (
              <div key={c.id}>
                {prev !== b && <BucketDivider label={label} first={i === 0} />}
                <ChallengeCard
                  challenge={c}
                  onClick={() => setSelectedChallenge(c)}
                />
              </div>
            );
          })
        )
      ) : isLoading ? (
        <EmptyState title="Загрузка…" />
      ) : events.length === 0 ? (
        <EmptyState
          title="Пока тихо"
          subtitle={
            filter === "tournaments"
              ? "Запишись на турнир — будет повод вернуться."
              : filter === "trainings"
                ? "Запишись на тренировку — прокачаешься к следующему турниру."
                : "Скоро здесь появятся турниры, тренировки и вызовы."
          }
        />
      ) : (
        events.map((e, i) => {
          const b = statusBucket(e);
          const prevRow = i > 0 ? events[i - 1] : undefined;
          const prev = prevRow ? statusBucket(prevRow) : null;
          const label = EVENT_BUCKET_LABEL[b] ?? "";
          return (
            <div key={`${e.kind}-${e.data.id}`}>
              {prev !== b && <BucketDivider label={label} first={i === 0} />}
              <EventCard event={e} onClick={() => setSelected(e)} />
            </div>
          );
        })
      )}

      <EventDetailSheet event={selected} onClose={() => setSelected(null)} />
      <ChallengeDetailSheet
        challengeId={selectedChallenge?.id ?? null}
        fallback={selectedChallenge}
        onClose={() => setSelectedChallenge(null)}
      />
      <CreateChallengeDialog
        open={challengeDialogOpen}
        onClose={() => setChallengeDialogOpen(false)}
      />
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  const next: ViewMode = view === "list" ? "calendar" : "list";
  const Icon = view === "list" ? CalendarDays : ListIcon;
  const title = view === "list" ? "Календарь" : "Список";
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={() => onChange(next)}
      style={{
        flex: "none",
        marginLeft: 16,
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: "1px solid var(--separator)",
        background: "var(--bg)",
        color: "var(--fg1)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      <Icon size={18} />
    </button>
  );
}

function CreateFab({ title, onClick }: { title: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      style={{
        flex: "none",
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: 0,
        background: "var(--brand-orange)",
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        boxShadow: "var(--sh-brand)",
      }}
    >
      <Plus size={18} />
    </button>
  );
}

function InvitesBanner() {
  const { user } = useAuth();
  const { data: invites = [] } = useMyInvites(user !== null);
  if (!user || invites.length === 0) return null;
  return (
    <div
      style={{
        margin: "0 16px 12px",
        background: "var(--brand-orange-ghost, rgba(255,90,31,0.08))",
        border: "1px solid var(--brand-orange)",
        borderRadius: "var(--r-md)",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div className="label" style={{ color: "var(--brand-orange)" }}>
        Приглашения в команду ({invites.length})
      </div>
      {invites.map((inv) => (
        <InviteRow key={inv.id} invite={inv} />
      ))}
    </div>
  );
}

function InviteRow({ invite }: { invite: TournamentInvite }) {
  const accept = useAcceptInvite();
  const decline = useDeclineInvite();
  const inviterName =
    [invite.inviter.first_name, invite.inviter.last_name].filter(Boolean).join(" ") ||
    invite.inviter.username ||
    `игрок #${invite.inviter.id}`;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="body">
          <b>{inviterName}</b> зовёт в команду
        </div>
        <div className="caption" style={{ color: "var(--fg2)" }}>
          Турнир: {invite.tournament_name}
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => accept.mutate(invite.id)}
        disabled={accept.isPending}
      >
        Принять
      </Button>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => decline.mutate(invite.id)}
        disabled={decline.isPending}
      >
        Отклонить
      </Button>
    </div>
  );
}

function BucketDivider({ label, first }: { label: string; first?: boolean }) {
  return (
    <div
      className="section-header"
      style={{
        margin: first ? "12px 16px 6px" : "18px 16px 6px",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span>{label}</span>
      <span
        aria-hidden
        style={{
          flex: 1,
          height: 1,
          background: "var(--separator)",
          opacity: 0.7,
        }}
      />
    </div>
  );
}

function EmptyState({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div
      style={{
        padding: "48px 32px",
        textAlign: "center",
        backgroundImage: "url(/brand/pattern-grid.svg)",
        backgroundPosition: "center",
        backgroundRepeat: "repeat",
        opacity: 0.9,
      }}
    >
      <h3 className="h3" style={{ margin: 0 }}>{title}</h3>
      {subtitle && (
        <p className="body" style={{ color: "var(--fg2)", marginTop: 6 }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}
